import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as authApi from '@/services/api/auth';
import * as authLib from '@/lib/auth';

export interface AuthContextType {
  isAuthenticated: boolean;
  user: authLib.StoredUser | null;
  isLoading: boolean;
  error: Error | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isClient: boolean;
  isSales: boolean;
  isGM: boolean;
  isOperations: boolean;
  isAdmin: boolean;
  /** True for any internal role (admin/sales/gm/operations) — coarse "can see internal-side panels" check. */
  canReview: boolean;
  /** True for roles that can mark Complete or Cancel — admin/operations only. */
  canBookOrCancel: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Interval for checking if token needs proactive refresh
 * Check every minute
 */
const PROACTIVE_REFRESH_INTERVAL_MS = 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const proactiveRefreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check for existing auth on mount
  useEffect(() => {
    const storedUser = authLib.getStoredUser();
    const token = authLib.getAccessToken();

    if (storedUser && token) {
      // User appears logged in, verify with API
      queryClient.setQueryData(['auth', 'user'], storedUser);
    }
    setIsInitialized(true);
  }, [queryClient]);

  // Proactive token refresh - check periodically
  useEffect(() => {
    // Only run proactive refresh if user is authenticated
    if (!authLib.isAuthenticated()) {
      return;
    }

    const checkAndRefreshToken = async () => {
      const token = authLib.getAccessToken();
      if (!token) return;

      // Check if token needs refresh (less than 5 minutes remaining)
      if (authLib.shouldRefreshToken()) {
        console.log('[Auth] Proactively refreshing token...');
        const refreshed = await authLib.refreshAccessToken();

        if (!refreshed) {
          console.log('[Auth] Proactive refresh failed, logging out...');
          // Clear auth data and redirect to login
          authLib.clearAuthData();
          queryClient.clear();
          window.location.href = '/login?reason=session_expired';
          return;
        }

        console.log('[Auth] Token refreshed successfully');
      }
    };

    // Check immediately on mount
    checkAndRefreshToken();

    // Set up periodic check
    proactiveRefreshTimerRef.current = setInterval(checkAndRefreshToken, PROACTIVE_REFRESH_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      if (proactiveRefreshTimerRef.current) {
        clearInterval(proactiveRefreshTimerRef.current);
      }
    };
  }, [queryClient]);

  // Sync auth state across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue === null) {
        // Token was cleared in another tab
        queryClient.clear();
        // Force page reload to re-initialize auth state
        window.location.href = '/login?message=You+have+been+logged+out.&reason=logged_out';
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [queryClient]);

  // Fetch current user (only if we have a token)
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      const token = authLib.getAccessToken();
      if (!token) throw new Error('No token');

      // Note: Backend doesn't have /auth/me endpoint yet,
      // so we'll use stored user data for now
      const storedUser = authLib.getStoredUser();
      if (!storedUser) throw new Error('No user data');
      return storedUser;
    },
    retry: false,
    enabled: isInitialized && authLib.isAuthenticated(),
    refetchInterval: false, // We use proactive refresh instead
  });

  const isAuthenticated = authLib.isAuthenticated();
  const currentUser = user || authLib.getStoredUser();

  const login = async (email: string, password: string) => {
    const result = await authApi.login({ email, password });

    // Store tokens
    authLib.setAccessToken(result.accessToken);
    authLib.setRefreshToken(result.refreshToken);
    authLib.setStoredUser(result.user);

    // Update cache
    queryClient.setQueryData(['auth', 'user'], result.user);
  };

  const logout = async () => {
    try {
      const refreshToken = authLib.getRefreshToken();
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // Ignore logout errors
    } finally {
      // Clear local storage
      authLib.clearAuthData();

      // Clear all query cache
      queryClient.clear();
    }
  };

  const hasRole = (role: string) => authLib.hasRole(currentUser || null, role);
  const hasAnyRole = (roles: string[]) => authLib.hasAnyRole(currentUser || null, roles);

  const isClient = hasRole('client');
  const isSales = hasRole('sales');
  const isGM = hasRole('gm');
  const isOperations = hasRole('operations');
  const isAdmin = hasRole('admin');
  const canReview = isAdmin || isSales || isGM || isOperations;
  const canBookOrCancel = isAdmin || isOperations;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user: currentUser,
        isLoading,
        error: error as Error | null,
        login,
        logout,
        hasRole,
        hasAnyRole,
        isClient,
        isSales,
        isGM,
        isOperations,
        isAdmin,
        canReview,
        canBookOrCancel,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
